import React, { useState, useEffect, useCallback, useRef } from "react";
import { canAccess } from "../utils/permissions";
import {
  getDashboards,
  saveDashboard,
  deleteDashboard as deleteDashboardAzure,
  getDashboardsLocal,
  saveDashboardsLocal,
} from "../services/azureDashboardService";
import DashboardWidgetWrapper from "./DashboardWidgetWrapper";

// Widget Adapters
import CashflowWidget from "./widgets/CashflowWidget";
import FinanzChartWidget from "./widgets/FinanzChartWidget";
import ProjectOverviewWidget from "./widgets/ProjectOverviewWidget";
import ProjectsFinanceWidget from "./widgets/ProjectsFinanceWidget";
import RessourcenplanungWidget from "./widgets/RessourcenplanungWidget";
import TeamWidget from "./widgets/TeamWidget";
import ZeiterfassungWidget from "./widgets/ZeiterfassungWidget";
import CrmRecentWidget from "./widgets/CrmRecentWidget";
import ControllingWidget from "./widgets/ControllingWidget";
import GleitzeitWidget from "./widgets/GleitzeitWidget";
import BudgetWidget from "./widgets/BudgetWidget";
import BudgetVerwaltungWidget from "./widgets/BudgetVerwaltungWidget";
import ReferencesWidget from "./widgets/ReferencesWidget";
import HistorischWidget from "./widgets/HistorischWidget";
import WebsiteWidget from "./widgets/WebsiteWidget";
import ConfigWidget from "./widgets/ConfigWidget";

// Widget-Registry mit Permissions
const WIDGET_REGISTRY = [
  { id: "cashflow", label: "Cashflow", permission: "finanzen", defaultW: 6, defaultH: 350 },
  { id: "finanzChart", label: "Finanz-Übersicht", permission: "finanzen", defaultW: 6, defaultH: 350 },
  { id: "projectOverview", label: "Projektübersicht", permission: "projekte", defaultW: 6, defaultH: 400 },
  { id: "projectsFinance", label: "Projekt-Finanzen", permission: "finanzen", defaultW: 6, defaultH: 400 },
  { id: "ressourcen", label: "Ressourcenplanung", permission: "ressourcenplanung", defaultW: 12, defaultH: 500 },
  { id: "team", label: "Team", permission: "team", defaultW: 6, defaultH: 350 },
  { id: "zeiterfassung", label: "Zeiterfassung", permission: "zeiterfassung", defaultW: 12, defaultH: 500 },
  { id: "crm", label: "CRM Kontakte", permission: "crm", defaultW: 4, defaultH: 350 },
  { id: "controlling", label: "Controlling", permission: "projekte", defaultW: 12, defaultH: 500 },
  { id: "gleitzeit", label: "Gleitzeit", permission: "zeiterfassung", defaultW: 3, defaultH: 250 },
  { id: "budget", label: "Budget Status", permission: "finanzen", defaultW: 6, defaultH: 400 },
  { id: "budgetVerwaltung", label: "Budget Verwaltung", permission: "finanzen", defaultW: 6, defaultH: 400 },
  { id: "references", label: "Referenzen", permission: "references", defaultW: 6, defaultH: 350 },
  { id: "historisch", label: "Historische Auswertung", permission: "finanzen", defaultW: 6, defaultH: 400 },
  { id: "website", label: "Website", permission: "website", defaultW: 6, defaultH: 400 },
  { id: "config", label: "Konfiguration", permission: "config", defaultW: 6, defaultH: 300 },
];

const getUserId = (user) =>
  user?.userDetails || user?.email || user?.userPrincipalName || user?.name || "anonymous";

const generateId = () => Date.now().toString();

// Calculate which widgets share a visual row based on their column spans
const getRows = (widgets) => {
  const sorted = [...widgets].sort((a, b) => a.order - b.order);
  const rows = [];
  let currentRow = [];
  let currentWidth = 0;

  for (const w of sorted) {
    if (currentWidth + w.w > 12 && currentRow.length > 0) {
      rows.push(currentRow);
      currentRow = [w];
      currentWidth = w.w;
    } else {
      currentRow.push(w);
      currentWidth += w.w;
    }
  }
  if (currentRow.length > 0) rows.push(currentRow);
  return rows;
};

const Dashboard = ({
  user,
  selectedEmployee,
  employees,
  contracts,
  allContracts,
  timeEntries,
  products,
  activityTypes,
  apiConnected,
  apiConfig,
  invoices,
  bookings,
  budgetProjections,
}) => {
  const [dashboards, setDashboards] = useState([]);
  const [activeDashboardId, setActiveDashboardId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showAddWidget, setShowAddWidget] = useState(false);
  const [showNewDashboard, setShowNewDashboard] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState("");
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editSnapshot, setEditSnapshot] = useState(null);
  const [renamingTab, setRenamingTab] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [resizing, setResizing] = useState(null);
  const renameInputRef = useRef(null);
  const gridRef = useRef(null);

  const userId = getUserId(user);
  const activeDashboard = dashboards.find((d) => d.id === activeDashboardId) || null;

  // Load dashboards on mount
  useEffect(() => {
    loadDashboards();
  }, [userId]);

  const loadDashboards = useCallback(async () => {
    setLoading(true);
    try {
      const remote = await getDashboards(userId);
      if (remote.length > 0) {
        setDashboards(remote);
        saveDashboardsLocal(userId, remote);
        if (!activeDashboardId || !remote.find((d) => d.id === activeDashboardId)) {
          setActiveDashboardId(remote[0].id);
        }
      } else {
        const local = getDashboardsLocal(userId);
        if (local.length > 0) {
          setDashboards(local);
          setActiveDashboardId(local[0].id);
        }
      }
    } catch {
      const local = getDashboardsLocal(userId);
      if (local.length > 0) {
        setDashboards(local);
        setActiveDashboardId(local[0].id);
      }
    } finally {
      setLoading(false);
    }
  }, [userId, activeDashboardId]);

  // Persist helper
  const persistDashboard = useCallback(
    async (dashboard) => {
      try {
        await saveDashboard(dashboard);
      } catch {
        // fallback to local
      }
      setDashboards((prev) => {
        const next = prev.map((d) => (d.id === dashboard.id ? dashboard : d));
        saveDashboardsLocal(userId, next);
        return next;
      });
    },
    [userId]
  );

  // Create new dashboard
  const handleCreateDashboard = useCallback(() => {
    const name = newDashboardName.trim();
    if (!name) return;

    const newDash = {
      id: generateId(),
      name,
      userId,
      widgets: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setDashboards((prev) => {
      const next = [...prev, newDash];
      saveDashboardsLocal(userId, next);
      return next;
    });
    setActiveDashboardId(newDash.id);
    setEditMode(true);
    setShowNewDashboard(false);
    setNewDashboardName("");

    saveDashboard(newDash).catch(() => {});
  }, [newDashboardName, userId]);

  // Delete dashboard
  const handleDeleteDashboard = useCallback(
    async (dashId) => {
      if (!confirm("Dashboard wirklich löschen?")) return;
      try {
        await deleteDashboardAzure(userId, dashId);
      } catch {
        // ignore
      }
      setDashboards((prev) => {
        const next = prev.filter((d) => d.id !== dashId);
        saveDashboardsLocal(userId, next);
        if (activeDashboardId === dashId) {
          setActiveDashboardId(next.length > 0 ? next[0].id : null);
        }
        return next;
      });
      setEditMode(false);
    },
    [userId, activeDashboardId]
  );

  // Edit mode
  const handleStartEdit = useCallback(() => {
    setEditSnapshot(JSON.stringify(activeDashboard));
    setEditMode(true);
  }, [activeDashboard]);

  const handleSave = useCallback(async () => {
    if (activeDashboard) {
      await persistDashboard({ ...activeDashboard, updatedAt: new Date().toISOString() });
    }
    setEditMode(false);
    setEditSnapshot(null);
  }, [activeDashboard, persistDashboard]);

  const handleCancel = useCallback(() => {
    if (editSnapshot) {
      const restored = JSON.parse(editSnapshot);
      setDashboards((prev) => prev.map((d) => (d.id === restored.id ? restored : d)));
    }
    setEditMode(false);
    setEditSnapshot(null);
  }, [editSnapshot]);

  // Widget operations
  const addWidget = useCallback(
    (registryItem) => {
      if (!activeDashboard) return;
      const newWidget = {
        instanceId: `w_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        widgetId: registryItem.id,
        label: registryItem.label,
        w: registryItem.defaultW,
        h: registryItem.defaultH || 300,
        order: activeDashboard.widgets.length,
      };
      const updated = {
        ...activeDashboard,
        widgets: [...activeDashboard.widgets, newWidget],
      };
      setDashboards((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
    },
    [activeDashboard]
  );

  const removeWidget = useCallback(
    (instanceId) => {
      if (!activeDashboard) return;
      const updated = {
        ...activeDashboard,
        widgets: activeDashboard.widgets.filter((w) => w.instanceId !== instanceId),
      };
      setDashboards((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
    },
    [activeDashboard]
  );

  // Drag & Drop reorder
  const handleDragStart = useCallback((index) => {
    setDraggedIndex(index);
  }, []);

  const handleDragOver = useCallback(
    (e, index) => {
      e.preventDefault();
      if (draggedIndex === null || draggedIndex === index) return;
      setDragOverIndex(index);
    },
    [draggedIndex]
  );

  const handleDrop = useCallback(
    (index) => {
      if (draggedIndex === null || !activeDashboard) return;
      const widgets = [...activeDashboard.widgets];
      const [moved] = widgets.splice(draggedIndex, 1);
      widgets.splice(index, 0, moved);
      const reordered = widgets.map((w, i) => ({ ...w, order: i }));
      const updated = { ...activeDashboard, widgets: reordered };
      setDashboards((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
      setDraggedIndex(null);
      setDragOverIndex(null);
    },
    [draggedIndex, activeDashboard]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  // --- Resize logic ---
  const handleResizeStart = useCallback(
    (instanceId, direction, e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!activeDashboard) return;

      const widget = activeDashboard.widgets.find((w) => w.instanceId === instanceId);
      if (!widget) return;

      // Find the right neighbor on the same row
      const rows = getRows(activeDashboard.widgets);
      const row = rows.find((r) => r.some((w) => w.instanceId === instanceId));
      const idx = row ? row.findIndex((w) => w.instanceId === instanceId) : -1;
      const neighbor = row && idx >= 0 && idx < row.length - 1 ? row[idx + 1] : null;

      // Set body cursor class
      const cursorClass =
        direction === "bottom" ? "resize-row" : direction === "corner" ? "resize-corner" : "";
      document.body.classList.add("dashboard-resizing");
      if (cursorClass) document.body.classList.add(cursorClass);

      setResizing({
        instanceId,
        direction,
        startX: e.clientX,
        startY: e.clientY,
        startW: widget.w,
        startH: widget.h || 300,
        neighborId: neighbor?.instanceId || null,
        neighborStartW: neighbor?.w || 0,
      });
    },
    [activeDashboard]
  );

  useEffect(() => {
    if (!resizing) return;

    const gridEl = gridRef.current;
    if (!gridEl) return;
    const gridWidth = gridEl.getBoundingClientRect().width;
    const gap = 16; // 1rem
    const colPixelWidth = (gridWidth - gap * 11) / 12;

    const onMouseMove = (e) => {
      const {
        direction,
        startX,
        startY,
        startW,
        startH,
        instanceId,
        neighborId,
        neighborStartW,
      } = resizing;

      let newW = startW;
      let newH = startH;
      let newNeighborW = neighborStartW;

      // Width resize
      if (direction === "right" || direction === "corner") {
        const deltaX = e.clientX - startX;
        const deltaCols = Math.round(deltaX / colPixelWidth);
        newW = Math.max(2, Math.min(12, startW + deltaCols));

        if (neighborId && neighborStartW > 0) {
          const totalCols = startW + neighborStartW;
          newNeighborW = totalCols - newW;
          if (newNeighborW < 2) {
            newNeighborW = 2;
            newW = totalCols - 2;
          }
          if (newW < 2) {
            newW = 2;
            newNeighborW = totalCols - 2;
          }
        }
      }

      // Height resize
      if (direction === "bottom" || direction === "corner") {
        const deltaY = e.clientY - startY;
        newH = Math.max(100, startH + deltaY);
      }

      setDashboards((prev) =>
        prev.map((d) => {
          if (d.id !== activeDashboardId) return d;
          return {
            ...d,
            widgets: d.widgets.map((w) => {
              if (w.instanceId === instanceId) {
                const update = { ...w };
                if (direction === "right" || direction === "corner") update.w = newW;
                if (direction === "bottom" || direction === "corner") update.h = newH;
                return update;
              }
              if (
                w.instanceId === neighborId &&
                (direction === "right" || direction === "corner")
              ) {
                return { ...w, w: newNeighborW };
              }
              return w;
            }),
          };
        })
      );
    };

    const onMouseUp = () => {
      document.body.classList.remove("dashboard-resizing", "resize-row", "resize-corner");
      setResizing(null);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [resizing, activeDashboardId]);

  // Tab rename
  const handleStartRename = useCallback(
    (dashId) => {
      const dash = dashboards.find((d) => d.id === dashId);
      if (!dash) return;
      setRenamingTab(dashId);
      setRenameValue(dash.name);
      setTimeout(() => renameInputRef.current?.focus(), 50);
    },
    [dashboards]
  );

  const handleFinishRename = useCallback(() => {
    if (!renamingTab) return;
    const name = renameValue.trim();
    if (name) {
      const dash = dashboards.find((d) => d.id === renamingTab);
      if (dash) {
        const updated = { ...dash, name };
        setDashboards((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
        persistDashboard(updated);
      }
    }
    setRenamingTab(null);
    setRenameValue("");
  }, [renamingTab, renameValue, dashboards, persistDashboard]);

  // Render widget content
  const renderWidgetContent = (widget) => {
    switch (widget.widgetId) {
      case "cashflow":
        return <CashflowWidget />;
      case "finanzChart":
        return <FinanzChartWidget />;
      case "projectOverview":
        return (
          <ProjectOverviewWidget
            contracts={contracts}
            employees={employees}
            products={products}
            invoices={invoices}
          />
        );
      case "projectsFinance":
        return <ProjectsFinanceWidget />;
      case "ressourcen":
        return <RessourcenplanungWidget />;
      case "team":
        return <TeamWidget employees={employees} user={user} />;
      case "zeiterfassung":
        return (
          <ZeiterfassungWidget
            currentWeek={new Date()}
            timeEntries={timeEntries}
            contracts={contracts}
            selectedEmployee={selectedEmployee}
          />
        );
      case "crm":
        return <CrmRecentWidget />;
      case "controlling":
        return <ControllingWidget employees={employees} />;
      case "gleitzeit":
        return <GleitzeitWidget selectedEmployee={selectedEmployee} />;
      case "budget":
        return <BudgetWidget />;
      case "budgetVerwaltung":
        return <BudgetVerwaltungWidget />;
      case "references":
        return <ReferencesWidget />;
      case "historisch":
        return <HistorischWidget />;
      case "website":
        return <WebsiteWidget />;
      case "config":
        return <ConfigWidget apiConfig={apiConfig} apiConnected={apiConnected} />;
      default:
        return <div className="widget-placeholder">Unbekanntes Widget: {widget.widgetId}</div>;
    }
  };

  // Filter registry by user permissions
  const availableWidgets = WIDGET_REGISTRY.filter((w) => canAccess(user, w.permission));

  if (loading) {
    return <div className="dashboard-loading">Dashboard wird geladen...</div>;
  }

  return (
    <div className="dashboard-container">
      {/* Tab Bar */}
      <div className="dashboard-tabs">
        <div className="tabs-left">
          {dashboards.map((dash) => (
            <div
              key={dash.id}
              className={`dashboard-tab${dash.id === activeDashboardId ? " active" : ""}`}
              onClick={() => {
                if (!editMode) setActiveDashboardId(dash.id);
              }}
              onDoubleClick={() => handleStartRename(dash.id)}
            >
              {renamingTab === dash.id ? (
                <input
                  ref={renameInputRef}
                  className="tab-rename-input"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onBlur={handleFinishRename}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleFinishRename();
                    if (e.key === "Escape") {
                      setRenamingTab(null);
                      setRenameValue("");
                    }
                  }}
                />
              ) : (
                dash.name
              )}
            </div>
          ))}
          <button
            className="dashboard-tab tab-add"
            onClick={() => setShowNewDashboard(true)}
            title="Neues Dashboard"
          >
            +
          </button>
        </div>

        <div className="tabs-right">
          {activeDashboard && editMode && (
            <>
              <button className="dashboard-btn" onClick={() => setShowAddWidget(true)}>
                Widget hinzufügen
              </button>
              <button className="dashboard-btn primary" onClick={handleSave}>
                Speichern
              </button>
              <button className="dashboard-btn" onClick={handleCancel}>
                Abbrechen
              </button>
              <button
                className="dashboard-btn danger"
                onClick={() => handleDeleteDashboard(activeDashboard.id)}
              >
                Löschen
              </button>
            </>
          )}
          {activeDashboard && !editMode && (
            <button className="dashboard-btn" onClick={handleStartEdit}>
              Bearbeiten
            </button>
          )}
        </div>
      </div>

      {/* Widget Grid */}
      {activeDashboard ? (
        <div className="dashboard-grid" ref={gridRef}>
          {activeDashboard.widgets
            .sort((a, b) => a.order - b.order)
            .map((widget, index) => (
              <DashboardWidgetWrapper
                key={widget.instanceId}
                widget={widget}
                editMode={editMode}
                isResizing={resizing !== null}
                onRemove={() => removeWidget(widget.instanceId)}
                onResizeStart={(dir, e) => handleResizeStart(widget.instanceId, dir, e)}
                isDraggedOver={dragOverIndex === index}
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={() => handleDrop(index)}
                onDragEnd={handleDragEnd}
              >
                {renderWidgetContent(widget)}
              </DashboardWidgetWrapper>
            ))}
          {activeDashboard.widgets.length === 0 && (
            <div className="dashboard-empty">
              {editMode
                ? "Klicke 'Widget hinzufügen' um Widgets zu diesem Dashboard hinzuzufügen."
                : "Dieses Dashboard hat noch keine Widgets. Klicke 'Bearbeiten' um Widgets hinzuzufügen."}
            </div>
          )}
        </div>
      ) : (
        <div className="dashboard-empty">
          Noch kein Dashboard vorhanden. Klicke '+' um ein neues Dashboard zu erstellen.
        </div>
      )}

      {/* New Dashboard Dialog */}
      {showNewDashboard && (
        <div className="dashboard-overlay" onClick={() => setShowNewDashboard(false)}>
          <div className="dashboard-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Neues Dashboard</h3>
            <input
              type="text"
              className="dashboard-input"
              placeholder="Dashboard-Name"
              value={newDashboardName}
              onChange={(e) => setNewDashboardName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreateDashboard();
                if (e.key === "Escape") setShowNewDashboard(false);
              }}
              autoFocus
            />
            <div className="dialog-actions">
              <button className="dashboard-btn primary" onClick={handleCreateDashboard}>
                Erstellen
              </button>
              <button className="dashboard-btn" onClick={() => setShowNewDashboard(false)}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Widget Dialog */}
      {showAddWidget && (
        <div className="dashboard-overlay" onClick={() => setShowAddWidget(false)}>
          <div
            className="dashboard-dialog widget-picker"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Widget hinzufügen</h3>
            <div className="widget-picker-grid">
              {availableWidgets.map((w) => (
                <button
                  key={w.id}
                  className="widget-picker-item"
                  onClick={() => {
                    addWidget(w);
                    setShowAddWidget(false);
                  }}
                >
                  <span className="widget-picker-label">{w.label}</span>
                  <span className="widget-picker-size">
                    {w.defaultW === 12
                      ? "Voll"
                      : w.defaultW === 8
                        ? "2/3"
                        : w.defaultW === 6
                          ? "1/2"
                          : w.defaultW === 4
                            ? "1/3"
                            : "1/4"}
                  </span>
                </button>
              ))}
            </div>
            <div className="dialog-actions">
              <button className="dashboard-btn" onClick={() => setShowAddWidget(false)}>
                Schliessen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
